import { useState } from "react";

/**
 * Host-only scoreboard panel.
 * Replaces the buzzer button. Shows live scores + per-player action buttons.
 * Highlights the current buzzer (winner) with +1 / Lock action buttons.
 */
const HostScoreboard = ({
  participants,
  scores,
  roundNumber,
  winner,
  suspendedNames,
  buzzerState,
  buzzerLocked,
  roundTimerSeconds,
  theme,
  mode = "solo",
  teams = [],
  teamScores = {},
  onAwardPoint,
  onSuspendWinner,
  onResetBuzzer,
  onNextRound,
  onResetGame,
  onEndGame,
  onKickPlayer,
  onTogglePlayerLock,
  onToggleBuzzerLock,
  onAdjustScore,
  onSetRoundTimer,
  onClearSuspensions,
  onMovePlayerToTeam,
  onAdjustTeamScore,
}) => {
  const [confirmKick, setConfirmKick] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [pointsToAward, setPointsToAward] = useState(1);
  // Wrong-answer deduction. Defaults to 0 so a "Wrong" click just locks without penalty,
  // but the host can bump it to deduct 1/2/… points per wrong answer.
  const [pointsToDeduct, setPointsToDeduct] = useState(0);

  const isTeamsMode = mode === "teams";

  // Build per-player rows (used in both modes; team mode groups them under team headers)
  const allNames = new Set([
    ...participants.filter((p) => !p.is_host).map((p) => p.name),
    ...Object.keys(scores || {}),
  ]);
  const rows = Array.from(allNames)
    .map((name) => {
      const p = participants.find((x) => x.name === name);
      return {
        name,
        score: scores?.[name] || 0,
        isLeft: !p || p.socket_id === null,
        isLocked: suspendedNames.includes(name),
        isWinner: winner?.name === name,
        isHostPlayer: p?.is_host || false,
        team_id: p?.team_id || null,
      };
    })
    .filter((r) => !r.isHostPlayer) // hide host from scoreboard
    .sort((a, b) => b.score - a.score);

  // Team-grouped rows: { team, members[], totalScore }
  const teamGroups = isTeamsMode
    ? teams
        .map((t) => ({
          team: t,
          members: rows.filter((r) => r.team_id === t.id),
          score: teamScores[t.id] || 0,
        }))
        .sort((a, b) => b.score - a.score)
    : [];
  const unassignedRows = isTeamsMode ? rows.filter((r) => !r.team_id) : [];

  return (
    <div className="w-full max-w-2xl mx-auto" data-testid="host-scoreboard">
      {/* Round header */}
      <div className="text-center mb-4">
        <p
          className="tracking-[0.3em] text-sm font-semibold"
          style={{ color: theme?.accentColor || "#00F0FF" }}
          data-testid="round-counter"
        >
          ROUND {roundNumber}
        </p>
        <h1
          className="text-4xl sm:text-5xl text-white"
          style={{ fontFamily: "Anton, sans-serif" }}
        >
          {buzzerLocked ? "BUZZERS LOCKED" : winner ? "BUZZED IN" : "WAITING FOR BUZZ"}
        </h1>
        {winner && (
          <p
            className="text-3xl sm:text-4xl mt-2 break-words"
            style={{
              fontFamily: "Anton, sans-serif",
              color: theme?.titleColor || "#FFEA00",
              textShadow: `0 0 20px ${theme?.titleColor || "#FFEA00"}66`,
            }}
            data-testid="current-winner-name"
          >
            {winner.name}
          </p>
        )}
      </div>

      {/* Master buzzer lock toggle */}
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        <button
          onClick={onToggleBuzzerLock}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm tracking-wide transition-all active:scale-95 border-2 ${
            buzzerLocked
              ? "bg-[#FF0055]/20 border-[#FF0055] text-[#FF0055]"
              : "bg-emerald-500/20 border-emerald-400 text-emerald-300"
          }`}
          data-testid="toggle-buzzer-lock-button"
          title={buzzerLocked ? "Unlock all buzzers" : "Lock all buzzers (e.g. while explaining)"}
        >
          {buzzerLocked ? (
            <>🔒 BUZZERS LOCKED — Tap to unlock</>
          ) : (
            <>🔓 BUZZERS ACTIVE — Tap to lock all</>
          )}
        </button>

        {suspendedNames && suspendedNames.length > 0 && (
          <button
            onClick={onClearSuspensions}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm tracking-wide transition-all active:scale-95 border-2 bg-amber-500/20 border-amber-400 text-amber-300 hover:bg-amber-500/30"
            data-testid="clear-suspensions-button"
            title="Unlock every player who is currently suspended for this round"
          >
            🔓 UNLOCK ALL ({suspendedNames.length})
          </button>
        )}
      </div>

      {/* Round timer status — auto-per-round setting (configured in lobby, tweakable here) */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
        <span
          className="text-xs tracking-[0.3em] text-[#A1A1AA] font-semibold"
          style={{ fontFamily: "Anton, sans-serif" }}
        >
          TIMER
        </span>
        <div
          className="inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-full px-1 py-1"
          data-testid="round-timer-inline-picker"
        >
          {[0, 5, 10, 15, 30, 60].map((secs) => {
            const isSelected = (roundTimerSeconds || 0) === secs;
            return (
              <button
                key={secs}
                onClick={() => onSetRoundTimer(secs)}
                className={`px-2.5 py-1 rounded-full text-xs font-bold tracking-wide transition-all active:scale-95 ${
                  isSelected
                    ? "text-black"
                    : "text-[#A1A1AA] hover:text-white"
                }`}
                style={
                  isSelected
                    ? { background: theme?.accentColor || "#00F0FF" }
                    : undefined
                }
                data-testid={`inline-timer-${secs}`}
                title={secs === 0 ? "No timer" : `${secs}s per question`}
              >
                {secs === 0 ? "OFF" : `${secs}s`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Winner action buttons (only visible when someone buzzed) */}
      {winner && (
        <div className="mb-6">
          {/* CORRECT row — award points then re-arm */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-full px-3 py-1.5">
              <span
                className="text-[11px] tracking-[0.3em] font-semibold"
                style={{ color: theme?.accentColor || "#00F0FF" }}
              >
                POINTS
              </span>
              <button
                onClick={() =>
                  setPointsToAward((p) => Math.max(0, (p ?? 1) - 1))
                }
                className="w-7 h-7 rounded-full bg-white/10 border border-white/20 text-white text-lg leading-none flex items-center justify-center hover:bg-white/20 active:scale-95"
                data-testid="points-dec-button"
                title="Decrease points"
              >
                −
              </button>
              <input
                type="number"
                value={pointsToAward}
                onChange={(e) => {
                  const raw = parseInt(e.target.value, 10);
                  if (Number.isNaN(raw)) return setPointsToAward(1);
                  setPointsToAward(Math.max(0, Math.min(10, raw)));
                }}
                className="w-12 bg-transparent text-center text-white text-xl font-bold tabular-nums focus:outline-none"
                style={{ fontFamily: "Anton, sans-serif" }}
                data-testid="points-value-input"
                min={0}
                max={10}
              />
              <button
                onClick={() =>
                  setPointsToAward((p) => Math.min(10, (p ?? 1) + 1))
                }
                className="w-7 h-7 rounded-full bg-white/10 border border-white/20 text-white text-lg leading-none flex items-center justify-center hover:bg-white/20 active:scale-95"
                data-testid="points-inc-button"
                title="Increase points"
              >
                +
              </button>
            </div>
            <button
              onClick={() => {
                onAwardPoint(Number.isFinite(pointsToAward) ? pointsToAward : 1);
                setPointsToAward(1);
              }}
              className="px-5 py-2.5 text-black font-bold text-sm rounded-full transition-all active:scale-95"
              style={{
                background: theme?.accentColor || "#00F0FF",
                boxShadow: `0 0 15px ${theme?.accentColor || "#00F0FF"}80`,
              }}
              data-testid="award-point-button"
              title={`Correct — award +${pointsToAward} point${pointsToAward === 1 ? "" : "s"}`}
            >
              ✓ CORRECT +{pointsToAward}
            </button>
          </div>

          {/* WRONG row — optional point deduction (default 0) then lock the winner */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
            <div className="flex items-center gap-2 bg-black/40 border border-[#FF0055]/40 rounded-full px-3 py-1.5">
              <span className="text-[11px] tracking-[0.3em] font-semibold text-[#FF0055]">
                DEDUCT
              </span>
              <button
                onClick={() =>
                  setPointsToDeduct((p) => Math.max(0, (p ?? 0) - 1))
                }
                className="w-7 h-7 rounded-full bg-white/10 border border-white/20 text-white text-lg leading-none flex items-center justify-center hover:bg-white/20 active:scale-95"
                data-testid="deduct-dec-button"
                title="Decrease deduction"
              >
                −
              </button>
              <input
                type="number"
                value={pointsToDeduct}
                onChange={(e) => {
                  const raw = parseInt(e.target.value, 10);
                  if (Number.isNaN(raw)) return setPointsToDeduct(0);
                  setPointsToDeduct(Math.max(0, Math.min(10, raw)));
                }}
                className="w-12 bg-transparent text-center text-white text-xl font-bold tabular-nums focus:outline-none"
                style={{ fontFamily: "Anton, sans-serif" }}
                data-testid="deduct-value-input"
                min={0}
                max={10}
              />
              <button
                onClick={() =>
                  setPointsToDeduct((p) => Math.min(10, (p ?? 0) + 1))
                }
                className="w-7 h-7 rounded-full bg-white/10 border border-white/20 text-white text-lg leading-none flex items-center justify-center hover:bg-white/20 active:scale-95"
                data-testid="deduct-inc-button"
                title="Increase deduction"
              >
                +
              </button>
            </div>
            <button
              onClick={() => {
                // Single atomic call: server deducts points (if > 0) then suspends the winner.
                const d = Number.isFinite(pointsToDeduct) ? pointsToDeduct : 0;
                onSuspendWinner(d);
                setPointsToDeduct(0);
              }}
              className="px-5 py-2.5 bg-[#FF0055] text-white font-bold text-sm rounded-full transition-all active:scale-95 shadow-[0_0_15px_rgba(255,0,85,0.5)]"
              data-testid="wrong-answer-button"
              title={
                pointsToDeduct > 0
                  ? `Wrong — deduct −${pointsToDeduct} and lock the buzzer`
                  : "Wrong — lock the buzzer (no deduction)"
              }
            >
              ✕ WRONG {pointsToDeduct > 0 ? `−${pointsToDeduct}` : ""}
            </button>
          </div>

          <div className="flex justify-center">
            <button
              onClick={onResetBuzzer}
              className="px-5 py-2 bg-white/10 text-white border border-white/20 font-bold text-sm rounded-full hover:bg-white/20 transition-all active:scale-95"
              data-testid="reset-buzzer-button"
              title="Cancel this buzz without locking or awarding"
            >
              ↺ Re-arm
            </button>
          </div>
        </div>
      )}

      {/* Scoreboard list */}
      <div className="bg-black/40 backdrop-blur-lg border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h3
            className="text-white font-bold tracking-wider"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            SCOREBOARD
          </h3>
          <span className="text-xs text-[#A1A1AA]">
            {isTeamsMode
              ? `${teams.length} team${teams.length !== 1 ? "s" : ""} · ${rows.length} player${rows.length !== 1 ? "s" : ""}`
              : `${rows.length} player${rows.length !== 1 ? "s" : ""}`}
          </span>
        </div>

        {(() => {
          // Renders a single player row. `idx` used for the position medal in solo mode; pass null in teams.
          const renderRow = (r, idx) => (
            <li
              key={r.name}
              className={`px-4 py-3 flex items-center gap-3 transition-colors ${
                r.isWinner ? "bg-[#FFEA00]/10" : ""
              } ${r.isLeft ? "opacity-40" : ""}`}
              data-testid={`scoreboard-row-${r.name}`}
            >
              <div className="w-7 text-center font-bold text-sm">
                {idx !== null && idx === 0 && r.score > 0 ? (
                  <span className="text-[#FFD700]">🏆</span>
                ) : (
                  <span className="text-[#A1A1AA]">{idx !== null ? idx + 1 : "•"}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-semibold truncate">{r.name}</span>
                  {r.isLocked && (
                    <span className="text-[10px] uppercase tracking-wide bg-[#FF0055]/30 text-[#FF0055] px-2 py-0.5 rounded-full">🔒 Locked</span>
                  )}
                  {r.isLeft && (
                    <span className="text-[10px] uppercase tracking-wide bg-white/10 text-[#A1A1AA] px-2 py-0.5 rounded-full">Left</span>
                  )}
                  {r.isWinner && (
                    <span className="text-[10px] uppercase tracking-wide bg-[#FFEA00]/30 text-[#FFEA00] px-2 py-0.5 rounded-full">Buzzed</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onAdjustScore(r.name, -1)}
                  className="w-7 h-7 rounded-full bg-white/5 hover:bg-[#FF0055]/20 hover:text-[#FF0055] text-[#A1A1AA] transition-colors text-lg font-bold leading-none flex items-center justify-center"
                  title={`Remove a point from ${r.name}`}
                  data-testid={`score-minus-${r.name}`}
                >
                  −
                </button>
                <div
                  className="text-2xl font-bold tabular-nums min-w-[2ch] text-center"
                  style={{ fontFamily: "Anton, sans-serif", color: theme?.accentColor || "#00F0FF" }}
                  data-testid={`score-${r.name}`}
                >
                  {r.score}
                </div>
                <button
                  onClick={() => onAdjustScore(r.name, 1)}
                  className="w-7 h-7 rounded-full bg-white/5 hover:bg-emerald-500/20 hover:text-emerald-300 text-[#A1A1AA] transition-colors text-lg font-bold leading-none flex items-center justify-center"
                  title={`Add a point to ${r.name}`}
                  data-testid={`score-plus-${r.name}`}
                >
                  +
                </button>
              </div>
              {/* Host can also move a player to a different team mid-game (teams mode only) */}
              {isTeamsMode && onMovePlayerToTeam && teams.length > 0 && (
                <select
                  value={r.team_id || "__none__"}
                  onChange={(e) => {
                    const v = e.target.value;
                    onMovePlayerToTeam(r.name, v === "__none__" ? null : v);
                  }}
                  className="bg-transparent text-white/70 border border-white/20 rounded-full px-2 py-1 text-[10px] cursor-pointer hover:text-white hover:border-white/40 outline-none"
                  title={`Move ${r.name} to another team`}
                  data-testid={`hs-move-${r.name}`}
                >
                  {teams.map((tt) => (
                    <option key={tt.id} value={tt.id} className="text-black">
                      {tt.name}
                    </option>
                  ))}
                  <option value="__none__" className="text-black">Unassigned</option>
                </select>
              )}
              <button
                onClick={() => onTogglePlayerLock(r.name)}
                className={`p-2 rounded-full transition-colors text-lg ${
                  r.isLocked
                    ? "text-[#FF0055] bg-[#FF0055]/15 hover:bg-[#FF0055]/25"
                    : "text-[#A1A1AA] hover:text-white hover:bg-white/10"
                }`}
                title={r.isLocked ? `Unlock ${r.name}` : `Lock out ${r.name}`}
                data-testid={`toggle-lock-${r.name}`}
              >
                {r.isLocked ? "🔒" : "🔓"}
              </button>
              <button
                onClick={() => setConfirmKick(r.name)}
                className="text-[#FF0055]/70 hover:text-[#FF0055] hover:bg-[#FF0055]/10 p-2 rounded-full transition-colors text-lg"
                title={`Remove ${r.name} from the room`}
                data-testid={`kick-button-${r.name}`}
              >
                ✕
              </button>
            </li>
          );

          // Empty state
          if (rows.length === 0) {
            return (
              <div className="p-6 text-center text-[#A1A1AA]">Waiting for players to join…</div>
            );
          }

          // Solo mode: flat list
          if (!isTeamsMode) {
            return <ul className="divide-y divide-white/5">{rows.map((r, idx) => renderRow(r, idx))}</ul>;
          }

          // Team mode: grouped by team, with a big TEAM header + team score
          return (
            <div className="divide-y divide-white/5">
              {teamGroups.map((g, gi) => (
                <div key={g.team.id} data-testid={`team-group-${g.team.id}`}>
                  <div
                    className="px-4 py-3 flex items-center justify-between"
                    style={{ background: `${g.team.color}18` }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {gi === 0 && g.score > 0 && <span className="text-lg">🏆</span>}
                      <span
                        className="font-bold tracking-wide truncate"
                        style={{ color: g.team.color, fontFamily: "Anton, sans-serif", fontSize: "1.1rem" }}
                        data-testid={`team-header-${g.team.id}`}
                      >
                        {g.team.name}
                      </span>
                      <span className="text-xs text-white/50">
                        {g.members.length} player{g.members.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {onAdjustTeamScore && (
                        <button
                          onClick={() => onAdjustTeamScore(g.team.id, -1)}
                          className="w-8 h-8 rounded-full bg-white/5 hover:bg-[#FF0055]/20 hover:text-[#FF0055] text-white/70 transition-colors text-xl font-bold leading-none flex items-center justify-center"
                          title={`Remove a point from ${g.team.name}`}
                          data-testid={`team-score-minus-${g.team.id}`}
                        >
                          −
                        </button>
                      )}
                      <div
                        className="text-3xl font-bold tabular-nums min-w-[2ch] text-center"
                        style={{ color: g.team.color, fontFamily: "Anton, sans-serif" }}
                        data-testid={`team-score-${g.team.id}`}
                      >
                        {g.score}
                      </div>
                      {onAdjustTeamScore && (
                        <button
                          onClick={() => onAdjustTeamScore(g.team.id, 1)}
                          className="w-8 h-8 rounded-full bg-white/5 hover:bg-emerald-500/20 hover:text-emerald-300 text-white/70 transition-colors text-xl font-bold leading-none flex items-center justify-center"
                          title={`Add a point to ${g.team.name}`}
                          data-testid={`team-score-plus-${g.team.id}`}
                        >
                          +
                        </button>
                      )}
                    </div>
                  </div>
                  {g.members.length === 0 ? (
                    <div className="px-4 py-2 text-xs text-white/40 italic">No players yet</div>
                  ) : (
                    <ul className="divide-y divide-white/5">
                      {g.members.map((r) => renderRow(r, null))}
                    </ul>
                  )}
                </div>
              ))}
              {unassignedRows.length > 0 && (
                <div data-testid="team-group-unassigned">
                  <div className="px-4 py-3 flex items-center justify-between bg-[#FFEA00]/10">
                    <span
                      className="font-bold tracking-wide text-[#FFEA00]"
                      style={{ fontFamily: "Anton, sans-serif" }}
                    >
                      UNASSIGNED
                    </span>
                    <span className="text-xs text-white/50">
                      {unassignedRows.length} player{unassignedRows.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <ul className="divide-y divide-white/5">
                    {unassignedRows.map((r) => renderRow(r, null))}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Game control buttons */}
      <div className="flex flex-wrap gap-2 justify-center mt-5">
        {!winner && buzzerState !== "buzzed" && (
          <button
            onClick={onNextRound}
            className="px-4 py-2 bg-white/10 text-white border border-white/20 text-sm font-semibold rounded-full hover:bg-white/20 transition-all active:scale-95"
            data-testid="next-round-button"
            title="Skip to next round without awarding"
          >
            ⤳ Next Round (no point)
          </button>
        )}
        <button
          onClick={() => setConfirmReset(true)}
          className="px-4 py-2 bg-white/5 text-[#A1A1AA] border border-white/10 text-sm font-semibold rounded-full hover:bg-white/10 hover:text-white transition-all active:scale-95"
          data-testid="reset-game-button"
        >
          ↺ Reset Scores
        </button>
        <button
          onClick={() => setConfirmEnd(true)}
          className="px-4 py-2 bg-[#FF0055]/20 text-[#FF0055] border border-[#FF0055]/40 text-sm font-semibold rounded-full hover:bg-[#FF0055]/30 transition-all active:scale-95"
          data-testid="end-game-button"
        >
          🏁 End Game & Reveal
        </button>
      </div>

      {/* Confirm kick modal */}
      {confirmKick && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setConfirmKick(null)}
        >
          <div
            className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-white text-2xl mb-2"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              REMOVE {confirmKick}?
            </h3>
            <p className="text-[#A1A1AA] text-sm mb-5">
              They will be disconnected and their score reset.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmKick(null)}
                className="px-4 py-2 text-[#A1A1AA] hover:text-white transition-colors"
                data-testid="kick-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onKickPlayer(confirmKick);
                  setConfirmKick(null);
                }}
                className="px-4 py-2 bg-[#FF0055] text-white rounded-full font-semibold hover:bg-[#FF3377] transition-colors"
                data-testid="kick-confirm"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm reset scores */}
      {confirmReset && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setConfirmReset(false)}
        >
          <div
            className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-white text-2xl mb-2"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              RESET ALL SCORES?
            </h3>
            <p className="text-[#A1A1AA] text-sm mb-5">
              Round goes back to 1. Players stay. This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmReset(false)}
                className="px-4 py-2 text-[#A1A1AA] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onResetGame();
                  setConfirmReset(false);
                }}
                className="px-4 py-2 bg-white text-black rounded-full font-semibold hover:bg-white/90 transition-colors"
                data-testid="reset-game-confirm"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm end game */}
      {confirmEnd && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setConfirmEnd(false)}
        >
          <div
            className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-white text-2xl mb-2"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              END GAME?
            </h3>
            <p className="text-[#A1A1AA] text-sm mb-5">
              Final scoreboard will be revealed to all players. You can reset and play again afterwards.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmEnd(false)}
                className="px-4 py-2 text-[#A1A1AA] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onEndGame();
                  setConfirmEnd(false);
                }}
                className="px-4 py-2 bg-[#FF0055] text-white rounded-full font-semibold hover:bg-[#FF3377] transition-colors"
                data-testid="end-game-confirm"
              >
                End & Reveal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostScoreboard;
